import { useEffect, useState } from 'react';
import { time } from '../api/client';
import { currentMonth, monthRange } from '../util/dates';


/**
 * Office vs home per person for a month, plus who is in where today.
 * Managers see their reporting line; HR sees everyone.
 */
export default function TeamAttendance({ onError }) {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    const { from, to } = monthRange(month);
    setLoading(true);
    time.summary(from, to)
      .then((r) => live && setRows(r || []))
      .catch((e) => onError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [month, onError]);

  const inToday = rows.filter((r) => r.todayIn);
  const office = inToday.filter((r) => r.todayMode === 'OFFICE').length;
  const home = inToday.filter((r) => r.todayMode === 'HOME').length;
  const totals = rows.reduce(
    (t, r) => ({ o: t.o + r.officeDays, h: t.h + r.homeDays }), { o: 0, h: 0 });
  const pct = (a, b) => (a + b ? Math.round((a / (a + b)) * 100) : 0);

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>Attendance</h2>
          <p className="muted">Office and work-from-home days, from punch-ins.</p>
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               style={{ width: 'auto' }} />
      </div>

      <div className="strip">
        <div className="strip-cell"><div className="strip-value">{office}</div><div className="strip-label">In office today</div></div>
        <div className="strip-cell"><div className="strip-value">{home}</div><div className="strip-label">Home today</div></div>
        <div className="strip-cell"><div className="strip-value">{rows.length - inToday.length}</div><div className="strip-label">Not punched in</div></div>
        <div className="strip-cell"><div className="strip-value">{pct(totals.o, totals.h)}%</div><div className="strip-label">Office days this month</div></div>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card empty"><p className="muted">No one in your reporting line yet.</p></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Employee</th><th>Today</th><th className="num">Office</th>
                <th className="num">Home</th><th>Split</th><th className="num">Hours</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const total = r.officeDays + r.homeDays;
                return (
                  <tr key={r.employeeId}>
                    <td>
                      {r.name}
                      <div className="small muted mono">{r.employeeCode}</div>
                    </td>
                    <td className="nowrap">
                      {r.todayIn ? (
                        <>
                          <span className={`mode-badge mode-${String(r.todayMode || 'none').toLowerCase()}`}>
                            {r.todayMode === 'HOME' ? 'WFH' : r.todayMode === 'OFFICE' ? 'WFO' : '—'}
                          </span>
                          <span className="small muted">
                            {' '}{String(r.todayIn).slice(11, 16)}
                            {r.todayOut && `–${String(r.todayOut).slice(11, 16)}`}
                          </span>
                        </>
                      ) : <span className="small muted">not in</span>}
                    </td>
                    <td className="num">{r.officeDays}</td>
                    <td className="num">{r.homeDays}</td>
                    <td style={{ minWidth: 120 }}>
                      {total > 0 ? (
                        <div className="split" title={`${pct(r.officeDays, r.homeDays)}% office`}>
                          <div className="split-office" style={{ width: `${pct(r.officeDays, r.homeDays)}%` }} />
                        </div>
                      ) : <span className="small muted">—</span>}
                      {r.unrecordedDays > 0 && (
                        <div className="small muted">{r.unrecordedDays} day(s) unrecorded</div>
                      )}
                    </td>
                    <td className="num small">{r.hours}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
